import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, Input, ViewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

/**
 * App tile card. The card itself is a navigation surface — the consumer puts
 * routerLink/(click) on the host element; keyboard activation re-dispatches a
 * click on the host so those handlers fire.
 */
@Component({
  selector: 'app-home-page-card',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
  ],
  templateUrl: './home-page-card.component.html',
  styleUrls: ['./home-page-card.component.scss']
})
export class HomePageCardComponent implements AfterViewInit {

  @Input() title: string;
  @Input() description: string;
  @Input() color: string;
  @Input() isPurchaseCard: boolean = false;
  @Input() hideDescription: boolean = false;

  @ViewChild('descriptionContainer') descriptionContainer: ElementRef<HTMLElement>;

  isDescriptionTruncated: boolean = false;
  showFullDescription: boolean = false;
  isHovered: boolean = false;
  isFocused: boolean = false;

  @HostListener('window:resize') onWindowResize() {
    this.checkDescriptionTruncation();
  }

  constructor(private host: ElementRef<HTMLElement>, private cdr: ChangeDetectorRef) { }

  ngAfterViewInit(): void {
    setTimeout(() => this.checkDescriptionTruncation());
  }

  onCardHover(hovered: boolean): void {
    this.isHovered = hovered;
  }

  onCardFocus(focused: boolean): void {
    this.isFocused = focused;
  }

  /** Keyboard activation — forwards to the host so the parent's routerLink/(click) fire. */
  activateCard(event: Event): void {
    event.preventDefault();
    this.host.nativeElement.click();
  }

  getIconGradient(): string {
    if (this.color) {
      if (!this.color.startsWith('#')) {
        return this.color;
      }
      return `linear-gradient(135deg, ${this.color}, ${this.adjustColorBrightness(this.color, -10)})`;
    }
    return 'linear-gradient(135deg, #35baf6, #2196f3)';
  }

  adjustColorBrightness(hex: string, percent: number): string {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.max(0, Math.min(255, (num >> 16) + amt));
    const G = Math.max(0, Math.min(255, (num >> 8 & 0x00FF) + amt));
    const B = Math.max(0, Math.min(255, (num & 0x0000FF) + amt));
    return '#' + ((R << 16) + (G << 8) + B).toString(16).padStart(6, '0');
  }

  toggleDescription(event: Event): void {
    event.stopPropagation(); // Don't navigate when toggling the description
    event.preventDefault();
    this.showFullDescription = !this.showFullDescription;
  }

  /** CSS line-clamp does the truncation; this only detects whether it kicked in. */
  private checkDescriptionTruncation(): void {
    const el = this.descriptionContainer?.nativeElement;
    if (!el || !this.description || this.hideDescription || this.showFullDescription) {
      return;
    }

    const truncated = el.scrollHeight > el.clientHeight + 1;
    if (truncated !== this.isDescriptionTruncated) {
      this.isDescriptionTruncated = truncated;
      this.cdr.markForCheck();
    }
  }
}
